import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createContractorAsync,
  getContractorAsync,
  updateContractorAsync
} from './contractorSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    }
  })
);

interface CreateEditContractor {
  id: number | null;
  name: string | null;
}

export default function ContractorCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedContractor } = useSelector(
    (state: RootState) => state.contractors
  );

  const params = useParams<{ contractorId: string }>();
  const contractorId = Number.parseInt(params.contractorId, 10);
  const isNewPage = !Boolean(contractorId);

  const [contractor, setContractor] = useState<CreateEditContractor>({
    id: null,
    name: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedContractor) {
      setContractor({
        ...contractor,
        id: selectedContractor.id,
        name: selectedContractor.name
      });
    }
  }, [selectedContractor]);

  useEffect(() => {
    if (!isNewPage) dispatch(getContractorAsync(contractorId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setContractor({
      ...contractor,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (contractor.name) {
        if (isNewPage) {
          // create contractor
          const createContractor = {
            name: contractor.name
          };
          await dispatch(createContractorAsync(createContractor)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('contractorCreateEdit.messages.createSuccess') as string
            })
          );
        } else {
          // update contractor
          const updateContractor = {
            id: contractor.id,
            name: contractor.name
          };
          await dispatch(updateContractorAsync(updateContractor)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('contractorCreateEdit.messages.updateSuccess') as string
            })
          );
        }
      } else {
        throw new Error(t('contractorCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('contractorCreateEdit.breadcrumb.contractor'),
          masterDataRef: 'contractors',
          masterDataSubName: selectedContractor?.name,
          masterDataSubRef: selectedContractor?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('contractorCreateEdit.title.new') : t('contractorCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('contractorCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            id="contractor-name"
            label={t('contractorCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={contractor.name ? contractor.name : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('contractorCreateEdit.buttons.add') : t('contractorCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
